import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMemo, type ReactNode } from "react";
import { CarTabIcon } from "@/components/CarImage";
import { Icon } from "@/components/Icon";
import { useAuth } from "@/lib/auth-context";
import { useUserData } from "@/lib/use-cars";
import { statusFor } from "@/lib/cars";

// React Router 7 port of components/AppShell.tsx. The Next.js original stays
// untouched until the Phase 6 cutover deletes it.

function avatarInitials(email: string | null | undefined): string {
  if (!email) return "AA";
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (local.slice(0, 2) || "AA").toUpperCase();
}

export function AppShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { cars, notifications } = useUserData();

  const counts = useMemo(() => {
    let warn = 0;
    let error = 0;
    for (const n of notifications) {
      const s = statusFor(n.date);
      if (s === "warn") warn++;
      else if (s === "error") error++;
    }
    return { warn, error };
  }, [notifications]);

  const isCars = pathname?.startsWith("/masini");
  const isNotifs = pathname?.startsWith("/notificari");
  const isProduse = pathname?.startsWith("/produse");
  const isPersons = pathname?.startsWith("/cont/persoane");
  const isAddresses = pathname?.startsWith("/cont/adrese");
  const isDocuments = pathname?.startsWith("/cont/documente");
  const isSettings = pathname?.startsWith("/cont/setari");
  // The hub at `/cont` and the profile editor at `/cont/profil` both light the
  // sidebar's "Profil" entry — the hub has no sidebar row of its own, since on
  // desktop the sidebar already lists everything the hub links to.
  const isProfile =
    pathname === "/cont" ||
    (pathname?.startsWith("/cont") &&
      !isPersons &&
      !isAddresses &&
      !isDocuments &&
      !isSettings);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <div className="app">
      <aside className="side">
        <Link to="/masini" className="side-brand">
          <span className="side-brand-mark">
            <img src="/simple_icon.svg" alt="" width={36} height={24} />
          </span>
          Asistent Auto
        </Link>

        <div className="side-eyebrow">Garaj</div>
        <ul className="side-list">
          <li>
            <Link
              to="/masini"
              className={`side-link${isCars ? " is-active" : ""}`}
            >
              <CarTabIcon />
              Mașinile mele
              <span className="count">{cars.length}</span>
            </Link>
          </li>
          <li>
            <Link
              to="/notificari"
              className={`side-link${isNotifs ? " is-active" : ""}`}
            >
              <Icon name="notifications_active" />
              Notificări
              <span
                className={`count${counts.error ? " error" : counts.warn ? " warn" : ""}`}
              >
                {notifications.length}
              </span>
            </Link>
          </li>
          <li>
            <Link
              to="/produse"
              className={`side-link${isProduse ? " is-active" : ""}`}
            >
              <Icon name="shopping_bag" />
              Produse
            </Link>
          </li>
        </ul>

        <div className="side-eyebrow">Cont</div>
        <ul className="side-list">
          <li>
            <Link
              to="/cont/persoane"
              className={`side-link${isPersons ? " is-active" : ""}`}
            >
              <Icon name="badge" />
              Persoane
            </Link>
          </li>
          <li>
            <Link
              to="/cont/adrese"
              className={`side-link${isAddresses ? " is-active" : ""}`}
            >
              <Icon name="home" />
              Adrese
            </Link>
          </li>
          <li>
            <Link
              to="/cont/documente"
              className={`side-link${isDocuments ? " is-active" : ""}`}
            >
              <Icon name="folder" />
              Documente
            </Link>
          </li>
          <li>
            <Link
              to="/cont/setari"
              className={`side-link${isSettings ? " is-active" : ""}`}
            >
              <Icon name="tune" />
              Setări
            </Link>
          </li>
          <li>
            <Link
              to="/cont/profil"
              className={`side-link${isProfile ? " is-active" : ""}`}
            >
              <Icon name="person" />
              Profil
            </Link>
          </li>
        </ul>

        <div className="side-foot">
          <nav className="side-info" aria-label="Informații">
            <Link to="/">Acasă</Link>
            <Link to="/functii">Funcții</Link>
            <Link to="/app">Aplicația web</Link>
            <Link to="/despre">Cine suntem</Link>
            <Link to="/ajutor">Informații utile</Link>
            <Link to="/intrebari">Întrebări frecvente</Link>
            <Link to="/termeni">Termeni și condiții</Link>
            <Link to="/confidentialitate">
              Politica de confidențialitate
            </Link>
          </nav>

          <div className="side-account">
            <span className="avatar">{avatarInitials(user?.email)}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                className="name"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {user?.email ?? "—"}
              </div>
              <div className="role">
                {cars.length} {cars.length === 1 ? "mașină" : "mașini"}
              </div>
            </div>
            <button
              type="button"
              className="more"
              onClick={handleSignOut}
              title="Deconectează-te"
            >
              <Icon name="logout" />
            </button>
          </div>
        </div>
      </aside>

      <div className="main">
        <div className="page">{children}</div>
        <nav className="bottom-nav" aria-label="Navigare principală">
          <Link
            to="/masini"
            className={`bottom-nav-item${isCars ? " is-active" : ""}`}
          >
            <span className="bottom-nav-pill">
              <CarTabIcon />
            </span>
            <span>Mașini</span>
          </Link>
          <Link
            to="/notificari"
            className={`bottom-nav-item${isNotifs ? " is-active" : ""}`}
          >
            <span className="bottom-nav-pill">
              <Icon name="notifications" />
            </span>
            <span>Notificări</span>
            {counts.warn + counts.error > 0 ? (
              <span className="bottom-nav-dot" />
            ) : null}
          </Link>
          <Link
            to="/produse"
            className={`bottom-nav-item${isProduse ? " is-active" : ""}`}
          >
            <span className="bottom-nav-pill">
              <Icon name="shopping_bag" />
            </span>
            <span>Produse</span>
          </Link>
          <Link
            to="/cont"
            className={`bottom-nav-item${isProfile || isPersons || isAddresses || isDocuments || isSettings ? " is-active" : ""}`}
          >
            <span className="bottom-nav-pill">
              <Icon name="person" />
            </span>
            <span>Cont</span>
          </Link>
        </nav>
      </div>
    </div>
  );
}
